import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
`;

const flowLine = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

export const PageWrapper = styled.div`
  width: 100%;
`;

// ─── Hero ──────────────────────────────────────────────────────────────────────

export const HeroSection = styled.section`
  padding: ${rem('120px')} ${rem('20px')} ${rem('80px')};
  background: linear-gradient(135deg, #0f172a 0%, #164e63 50%, #0e7490 100%);
  position: relative;
  overflow: hidden;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    padding: ${rem('100px')} ${rem('16px')} ${rem('60px')};
  }
`;

export const HeroGlowOrb = styled.div`
  position: absolute;
  top: 50%;
  right: 5%;
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(6, 182, 212, 0.15) 0%,
    transparent 70%
  );
  border-radius: 50%;
  animation: ${float} 6s ease-in-out infinite;
  pointer-events: none;
  transform: translateY(-50%);

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    width: 300px;
    height: 300px;
    right: -10%;
    top: 30%;
  }
`;

export const HeroContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem('60px')};
  align-items: center;
  position: relative;
  z-index: 1;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    grid-template-columns: 1fr;
    gap: ${rem('40px')};
    text-align: center;
  }
`;

export const HeroContent = styled.div`
  width: 100%;
  max-width: 100%;
`;

export const HeroTitle = styled.h1`
  font-family: 'MontserratBlack', sans-serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  line-height: 1.1;
  color: #ffffff;
  margin-bottom: ${rem('20px')};
  font-weight: 800;
  letter-spacing: -1px;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.5rem);
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: ${rem('32px')};
  font-weight: 500;
`;

export const HeroAnimationWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    max-width: 380px;
    margin: 0 auto;
  }

  @media ${({ theme }) => theme.mediaQueries.smallMax} {
    max-width: 280px;
  }
`;

// ─── Services ──────────────────────────────────────────────────────────────────

export const ServicesSection = styled.section`
  padding: ${rem('100px')} ${rem('20px')} ${rem('80px')};
  background: #f8fafc;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    padding: ${rem('70px')} ${rem('16px')} ${rem('60px')};
  }
`;

export const SectionContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
`;

export const SectionTitle = styled.h2`
  font-family: 'MontserratBlack', sans-serif;
  font-size: clamp(1.8rem, 3.5vw, 2.5rem);
  line-height: 1.3;
  color: ${({ $light }) => ($light ? '#ffffff' : '#1f2937')};
  margin-bottom: ${rem('16px')};
  text-align: center;
  font-weight: 800;
  letter-spacing: -0.5px;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

export const SectionSubtitle = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.15rem);
  color: ${({ $light }) => ($light ? 'rgba(255, 255, 255, 0.7)' : '#6b7280')};
  text-align: center;
  max-width: 700px;
  margin: 0 auto ${rem('48px')};
  line-height: 1.6;
`;

export const ServicesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${rem('24px')};
`;

export const ServiceCard = styled.div`
  background: white;
  padding: ${rem('28px')};
  border-radius: 16px;
  border-left: 4px solid #06b6d4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  width: calc(33.333% - ${rem('16px')});

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    width: 100%;
    border-left: none;
    border-top: 4px solid #06b6d4;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(6, 182, 212, 0.15);
  }
`;

export const ServiceIcon = styled.div`
  margin-bottom: ${rem('16px')};
  display: flex;
  align-items: center;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    justify-content: center;
  }
`;

export const ServiceTitle = styled.h3`
  font-size: ${rem('18px')};
  color: #1f2937;
  margin-bottom: ${rem('8px')};
  font-weight: 700;
`;

export const ServiceDescription = styled.p`
  font-size: ${rem('15px')};
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
`;

// ─── Tech Stack ────────────────────────────────────────────────────────────────

export const TechStackSection = styled.section`
  padding: ${rem('100px')} ${rem('20px')};
  background: #0f172a;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    padding: ${rem('70px')} ${rem('16px')};
  }
`;

export const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${rem('16px')};
  max-width: 900px;
  margin: 0 auto;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    grid-template-columns: repeat(3, 1fr);
  }

  @media ${({ theme }) => theme.mediaQueries.smallMax} {
    grid-template-columns: repeat(2, 1fr);
    gap: ${rem('12px')};
  }
`;

export const TechCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: ${rem('20px')} ${rem('12px')};
  text-align: center;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;

  svg {
    margin-bottom: ${rem('10px')};
  }

  &:hover {
    border-color: rgba(6, 182, 212, 0.5);
    box-shadow: 0 0 20px rgba(6, 182, 212, 0.1);
    transform: translateY(-2px);
  }
`;

export const TechLogo = styled.div`
  width: ${rem('44px')};
  height: ${rem('44px')};
  border-radius: 50%;
  background: linear-gradient(135deg, #06b6d4 0%, #14b8a6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${rem('10px')};
  font-size: ${rem('18px')};
  font-weight: 800;
  color: white;
`;

export const TechName = styled.div`
  font-size: ${rem('14px')};
  color: #e2e8f0;
  font-weight: 600;
  margin-bottom: ${rem('4px')};
`;

export const TechCategory = styled.div`
  font-size: ${rem('11px')};
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// ─── Architecture / Pipeline ───────────────────────────────────────────────────

export const ArchitectureSection = styled.section`
  padding: ${rem('100px')} ${rem('20px')} ${rem('120px')};
  background: white;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    padding: ${rem('70px')} ${rem('16px')} ${rem('80px')};
  }
`;

export const PipelineContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  max-width: 1100px;
  margin: 0 auto;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: ${rem('28px')};
    left: calc(10% + ${rem('28px')});
    right: calc(10% + ${rem('28px')});
    height: 3px;
    background: linear-gradient(90deg, #06b6d4, #14b8a6, #06b6d4);
    background-size: 200% 100%;
    animation: ${flowLine} 3s linear infinite;
    z-index: 0;
  }

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    grid-template-columns: 1fr;
    gap: ${rem('40px')};
    max-width: 400px;

    &::before {
      display: none;
    }
  }
`;

export const PipelineStep = styled.div`
  text-align: center;
  position: relative;
  z-index: 1;
`;

export const PipelineStepNumber = styled.div`
  width: ${rem('56px')};
  height: ${rem('56px')};
  border-radius: 50%;
  background: white;
  border: 3px solid #06b6d4;
  color: #0891b2;
  font-size: ${rem('22px')};
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${rem('20px')};
  box-shadow: 0 4px 12px rgba(6, 182, 212, 0.2);
`;

export const PipelineStepTitle = styled.h3`
  font-size: ${rem('18px')};
  color: #1f2937;
  margin-bottom: ${rem('8px')};
  font-weight: 700;
`;

export const PipelineStepDescription = styled.p`
  font-size: ${rem('14px')};
  color: #6b7280;
  line-height: 1.6;
  padding: 0 ${rem('8px')};
`;

// ─── Benefits ──────────────────────────────────────────────────────────────────

export const BenefitsSection = styled.section`
  padding: ${rem('100px')} ${rem('20px')};
  background: #ecfeff;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    padding: ${rem('70px')} ${rem('16px')};
  }
`;

export const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem('24px')};
  max-width: 900px;
  margin: 0 auto;

  @media ${({ theme }) => theme.mediaQueries.smallMax} {
    grid-template-columns: 1fr;
  }
`;

const borderColors = ['#06B6D4', '#14B8A6', '#0891B2', '#164E63'];

export const BenefitCard = styled.div`
  background: white;
  padding: ${rem('32px')};
  border-radius: 16px;
  border-top: 4px solid ${({ $index }) => borderColors[$index % 4]};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(6, 182, 212, 0.12);
  }
`;

export const BenefitStat = styled.div`
  font-family: 'MontserratBlack', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  background: linear-gradient(135deg, #06b6d4 0%, #14b8a6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${rem('12px')};
  line-height: 1;
`;

export const BenefitIcon = styled.div`
  margin-bottom: ${rem('8px')};
`;

export const BenefitTitle = styled.h3`
  font-size: ${rem('18px')};
  color: #1f2937;
  margin-bottom: ${rem('8px')};
  font-weight: 700;
`;

export const BenefitDescription = styled.p`
  font-size: ${rem('15px')};
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
`;

// ─── CTA ───────────────────────────────────────────────────────────────────────

export const CTASection = styled.section`
  padding: ${rem('100px')} ${rem('20px')};
  background: #0f172a;
  position: relative;
  overflow: hidden;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    padding: ${rem('70px')} ${rem('16px')};
  }
`;

export const CTACard = styled.div`
  max-width: 650px;
  margin: 0 auto;
  background: white;
  padding: ${rem('48px')};
  border-radius: 16px;
  border-top: 4px solid transparent;
  border-image: linear-gradient(90deg, #06b6d4, #14b8a6) 1;
  border-image-slice: 1;
  text-align: center;

  @media ${({ theme }) => theme.mediaQueries.smallMax} {
    padding: ${rem('32px')} ${rem('24px')};
  }
`;

export const CTATitle = styled.h2`
  font-family: 'MontserratBlack', sans-serif;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  color: #1f2937;
  margin-bottom: ${rem('16px')};
  font-weight: 800;
  letter-spacing: -0.5px;
`;

export const CTADescription = styled.p`
  font-size: ${rem('16px')};
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: ${rem('32px')};
`;

export const CTADeliverables = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${rem('32px')} 0;
  text-align: left;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

export const DeliverableItem = styled.li`
  font-size: ${rem('15px')};
  color: #374151;
  padding: ${rem('10px')} 0;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: ${rem('10px')};

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '✓';
    color: #06b6d4;
    font-weight: 700;
    font-size: ${rem('18px')};
  }
`;

export const CTAButton = styled.button`
  width: 100%;
  max-width: 400px;
  padding: ${rem('16px')} ${rem('32px')};
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
  font-size: ${rem('18px')};
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: block;
  margin: 0 auto;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(6, 182, 212, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

// ─── FAQ ───────────────────────────────────────────────────────────────────────

export const FAQSection = styled.section`
  padding: ${rem('60px')} ${rem('20px')} ${rem('100px')};
  background: #f8fafc;

  @media ${({ theme }) => theme.mediaQueries.mediumMax} {
    padding: ${rem('50px')} ${rem('16px')} ${rem('70px')};
  }
`;

export const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

export const FAQItem = styled.div`
  background: white;
  padding: ${rem('24px')};
  border-radius: 12px;
  margin-bottom: ${rem('16px')};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

export const FAQQuestion = styled.h3`
  font-size: ${rem('17px')};
  color: #1f2937;
  margin-bottom: ${rem('10px')};
  font-weight: 700;
`;

export const FAQAnswer = styled.p`
  font-size: ${rem('16px')};
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
`;
